import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { FaCalendarAlt } from 'react-icons/fa';
import Badge from 'react-bootstrap/Badge';
import Form from 'react-bootstrap/Form';
import DateTimePickerCal from './DateTimePickerCal';
import httpService from '../../services/httpService';
import eventBus from '../../services/eventBus';

const TakeawayPractice = forwardRef(({ practice, keyTakeawayId, isEditing = false, onSaved, onSavingChange }, ref) => {
  const [form, setForm] = useState({
    title: practice?.title || '',
    body: practice?.body || '',
    practice_date_time: practice?.practice_date_time ? new Date(practice.practice_date_time) : null,
  });
  const [errors, setErrors] = useState({});
  const bodyGroupRef = useRef(null);
  const dateGroupRef = useRef(null);

  useEffect(() => {
    setForm({
      title: practice?.title || ' ',
      body: practice?.body || '',
      practice_date_time: practice?.practice_date_time ? new Date(practice.practice_date_time) : null,
    });
  }, [practice?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  useImperativeHandle(ref, () => ({
    save: (options = {}) => {
      const isCreate = !practice?.id;
      const newErrors = {};
      if (!form.body || !form.body.trim()) newErrors.body = 'Please describe how you will try this.';
      if (!form.practice_date_time) newErrors.practice_date_time = 'Practice date & time is required.';
      if (Object.keys(newErrors).length) {
        setErrors(newErrors);
        eventBus.emit('toast', { type: 'danger', message: Object.values(newErrors)[0] });
        (newErrors.body ? bodyGroupRef : dateGroupRef).current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      setErrors({});

      const payload = {
        title: form.title??' ',
        body: form.body,
        type: 'custom',
        practice_date_time: new Date(form.practice_date_time).toISOString(),
        ...(options.skillIds?.length ? { skill_ids: options.skillIds } : {}),
      };

      onSavingChange?.(true);

      const request = isCreate
        ? httpService.post(`/dash/key-takeaway/${keyTakeawayId}/practices`, payload)
        : httpService.put(`/dash/key-takeaway/${keyTakeawayId}/practices/${practice.id}`, payload);

      request
        .then(res => onSaved?.(res.data.data))
        .catch(err => console.error('Error saving practice:', err))
        .finally(() => onSavingChange?.(false));
    },
  }));

  if (!isEditing) {
    if (!practice?.id) return null;
    return (
      <div>
        <h5>{practice.title || 'Practice'}</h5>
        <div className="d-flex align-items-center gap-2 mb-3">
          <Badge bg={practice.completed ? 'success' : 'secondary'}>
            {practice.completed ? 'Completed' : 'In Progress'}
          </Badge>
          {practice.practice_date_time && (
            <small className="text-muted">
              Scheduled: {new Date(practice.practice_date_time).toLocaleDateString()}
            </small>
          )}
          {practice.completion_date_time && (
            <small className="text-muted">
              Completed: {new Date(practice.completion_date_time).toLocaleDateString()}
            </small>
          )}
        </div>
        {practice.body && (
          <div className="mb-3">
            <strong>Notes:</strong>
            <p>{practice.body}</p>
          </div>
        )}
        {practice.type && <p><strong>Type:</strong> {practice.type}</p>}
      </div>
    );
  }

  return (
    <Form>
      {/*<Form.Group className="mb-3">*/}
      {/*  <Form.Label>Title</Form.Label>*/}
      {/*  <Form.Control*/}
      {/*    type="text"*/}
      {/*    name="title"*/}
      {/*    value={form.title}*/}
      {/*    onChange={handleChange}*/}
      {/*    placeholder="Enter practice title"*/}
      {/*    isInvalid={!!errors.title}*/}
      {/*  />*/}
      {/*  <Form.Control.Feedback type="invalid">{errors.title}</Form.Control.Feedback>*/}
      {/*</Form.Group>*/}
      <Form.Group className="mb-3" ref={bodyGroupRef}>
        <Form.Label>How will you try this ?<br /><small className="text-muted">Your version, in your situation. A sentence or two, not a polished plan.</small></Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="body"
          value={form.body}
          onChange={handleChange}
          placeholder="Enter practice description"
          isInvalid={!!errors.body}
        />
        <Form.Control.Feedback type="invalid">{errors.body}</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" ref={dateGroupRef}>
        <Form.Label>When will you try this ?<br /><small className="text-muted">A date and time so the practice doesn't drift. We schedule a reminder an hour before; you can add more on the Reminders tab after saving.</small></Form.Label>
        <div className="input-group">
          <DateTimePickerCal
            value={form.practice_date_time}
            onChange={(date) => setForm(prev => ({ ...prev, practice_date_time: date }))}
            isInvalid={!!errors.practice_date_time}
            placeholder="Select date and time..."
          />
          <span className="input-group-text border-success"><FaCalendarAlt /></span>
        </div>
        {errors.practice_date_time && (
          <div className="invalid-feedback d-block">{errors.practice_date_time}</div>
        )}
      </Form.Group>
    </Form>
  );
});

TakeawayPractice.displayName = 'TakeawayPractice';
export default TakeawayPractice;
